import React, { createContext, useState, useEffect, useCallback, useRef, ReactNode, useContext } from 'react';
import { badgeService } from '../services/badgeService';
import { transformBadgeDefinitionFromAPI } from '../utils/badgeTransformers';
import type { BadgeDefinition } from '../types/badges';
import { UserContext } from './UserContext';

export type Badge = BadgeDefinition;

interface BadgeContextType {
    badges: Badge[];
    fetchBadges: () => Promise<Badge[]>;
    isLoading: boolean;
}

export const BadgeContext = createContext<BadgeContextType>({
    badges: [],
    fetchBadges: async () => [],
    isLoading: false,
});

export const BadgeProvider = ({ children }: { children: ReactNode }) => {
    const [badges, setBadges] = useState<Badge[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const isInitializedRef = useRef(false);
    const { isAuthenticated } = useContext(UserContext);

    const fetchBadges = useCallback(async () => {
        if (!isAuthenticated || isInitializedRef.current) {
            return badges;
        }
        isInitializedRef.current = true;
        setIsLoading(true);
        try {
            const data = await badgeService.getBadges();
            const transformed = data.map(transformBadgeDefinitionFromAPI);
            setBadges(transformed);
            return transformed;
        } catch (err) {
            console.error('Failed to fetch badges', err);
            setBadges([]);
            return [];
        } finally {
            setIsLoading(false);
        }
    }, [isAuthenticated]);

    useEffect(() => {
        if (isAuthenticated) {
            if (!isInitializedRef.current) {
                fetchBadges();
            }
        } else {
            setBadges([]);
            isInitializedRef.current = false;
        }
    }, [isAuthenticated, fetchBadges]);

    return (
        <BadgeContext.Provider value={{ badges, fetchBadges, isLoading }}>
            {children}
        </BadgeContext.Provider>
    );
};

export const useBadges = () => React.useContext(BadgeContext);
